import { setBlockType } from 'prosemirror-commands';
import { InputRule } from 'prosemirror-inputrules';
import { Mark, MarkType, Node, NodeType, Schema } from 'prosemirror-model';
import { DOMParser as PMDOMParser } from 'prosemirror-model';
import { liftListItem } from 'prosemirror-schema-list';
import { EditorState, NodeSelection, PluginKey, TextSelection } from 'prosemirror-state';
import { findParentNode } from 'prosemirror-utils';
import { EditorView } from 'prosemirror-view';

import { getEditorContainerInnerWidth, getEditorWidth, MarkdownParser } from '@lyearn/lygo-core';

import { NODE_CONTAINER_CLASSNAME } from '../components/ResizeContainer';
import { toggleAddMenuList } from '../plugins/AddMenu/commands';
import {
  AddMenuPluginKey,
  MAX_MATCH,
  MAX_MENU_ITEMS,
  MENU_ITEM_HEIGHT,
  MENU_LIST_Y_PADDING,
} from '../plugins/AddMenu/consts';
import { addMenuItemType, addMenuProps } from '../plugins/AddMenu/types';
import { getAddMenuDefaultProps } from '../plugins/AddMenu/utils';
import { BLOCK_MENU_BUTTON_MARGIN, BLOCK_MENU_BUTTON_WIDTH } from '../plugins/BlockMenu/consts';
import { isMarkActive } from '../plugins/SelectionMenu/commands';
import { PopperProps } from '../types';
import { blockType } from '../types/menus';

enum selectionType {
  TEXT = 'text',
  NODE = 'node',
}

export const getIsInNodeFn = (nodeName: any) => {
  return (state: EditorView['state']) => {
    const $head = state.selection.$head;
    for (let d = $head.depth; d > 0; d--) {
      if ($head.node(d).type === state.schema.nodes[nodeName]) {
        return true;
      }
    }

    return false;
  };
};

export const isList = (node: Node, schema: Schema) => {
  return [schema.nodes.bullet_list, schema.nodes.ordered_list].includes(node.type);
};

export const isTable = (node: Node, schema: Schema) => {
  return [
    schema.nodes.table,
    schema.nodes.table_row,
    schema.nodes.table_header,
    schema.nodes.table_cell,
  ].includes(node.type);
};

export const isTabs = (node: Node, schema: Schema) => {
  return [schema.nodes.tab_view, schema.nodes.tab_panel, schema.nodes.tab].includes(node.type);
};

export const isToggleList = (node: Node, schema: Schema) => {
  return [
    schema.nodes.toggle_list,
    schema.nodes.toggle_list_item,
    schema.nodes.toggle_block_content,
  ].includes(node.type);
};

export const isCheckList = (node: Node, schema: Schema) => {
  return [schema.nodes.check_list, schema.nodes.check_list_item].includes(node.type);
};

export const isInCallout = getIsInNodeFn('callout');

export const isInCode = (state: EditorState) => {
  return getIsInNodeFn('code_block')(state) || isMarkActive('code_inline', state);
};

export const isInCodeBlock = (state: EditorState) => {
  return getIsInNodeFn('code_block')(state);
};

export const isInBlockquote = getIsInNodeFn('blockquote');

export const isInToggleListHeading = getIsInNodeFn('toggle_block_heading');

export const isInCheckListItem = getIsInNodeFn('check_list_item');

export const isInTable = (view?: EditorView, position?: number) => {
  if (view && position) {
    return isTable(view.state.doc.resolve(position).parent, view.state.schema);
  }

  return false;
};

export const isInToggleList = (view?: EditorView, position?: number) => {
  if (view && position) {
    return isToggleList(view.state.doc.resolve(position).parent, view.state.schema);
  }

  return false;
};

export const isInTabs = (view?: EditorView, position?: number) => {
  if (view && position) {
    return isTabs(view.state.doc.resolve(position).parent, view.state.schema);
  }

  return false;
};

export const getDomNode = (view: EditorView, pos: number) => {
  const domAtPos = view.domAtPos(pos);
  if (domAtPos.node) {
    return domAtPos.node.nodeName === '#text' ? domAtPos.node.parentElement : domAtPos.node;
  }

  return null;
};

// Need any because pmViewDesc property is not there in the Node types
// from 'prosemirror-model'.
export const getCurrentSelectionPMViewDesc = (view: any, pos: number) => {
  let currentSelectedNode = view.domAtPos(pos).node;
  while (!currentSelectedNode.pmViewDesc) {
    currentSelectedNode = currentSelectedNode.parentNode;
  }

  return currentSelectedNode.pmViewDesc;
};

// Need any because pmViewDesc property is not there in the Node types
// from 'prosemirror-model'.
export const getCurrentSelectionNode = (view: any, pos: number) => {
  return getCurrentSelectionPMViewDesc(view, pos).node;
};

export const getElementPos = (anchorEl: PopperProps['anchorEl'], view: EditorView) => {
  const getBoundingClientRect = (anchorEl as HTMLElement).getBoundingClientRect();
  // +1 needed to get the right pos value of selected dom node. Else it will give
  // the pos element of the above dom node.
  return view.posAtCoords({
    left: getBoundingClientRect.left + 1,
    top: getBoundingClientRect.top + 1,
  });
};

export const selectNode = (anchorEl: PopperProps['anchorEl'], view: EditorView) => {
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.inside !== undefined) {
    setSelection(view, selectionType.NODE, elementPos.inside);
  }
};

// 40px is a height of one menu-item and 24px is the padding of the Add Menu. 60px for extra margin
// for the header. MAX_MENU_ITEMS is set for the overflow scroll in the Menulist.
export const MAX_MENU_HEIGHT = MAX_MENU_ITEMS * MENU_ITEM_HEIGHT + MENU_LIST_Y_PADDING + 60;

export const calculateOffset = (elementPos: any, getBoundingClientRect: any) => {
  // It will calculate the difference of the left and top positions between
  // the '/' text node and the block element in which '/' is typed. This
  // difference will help the popper element to put the Add Menu just above
  // or below the '/' text node.

  return [
    elementPos.left - getBoundingClientRect.left,
    elementPos.top < MAX_MENU_HEIGHT
      ? elementPos.bottom - getBoundingClientRect.bottom
      : elementPos.top - getBoundingClientRect.top,
  ];
};

export const calculateTransformOrigin = (elementPos: any) => {
  if (elementPos.top < MAX_MENU_HEIGHT) {
    return document.dir === 'ltr' ? 'bottom-start' : 'bottom-end';
  } else {
    return document.dir === 'ltr' ? 'top-start' : 'top-end';
  }
};

// ref: https://github.com/ProseMirror/prosemirror-inputrules/blob/master/src/inputrules.js#L82
export function run(view: EditorView, from: number, to: number, regex: any, handler: any) {
  const $from = view.state.doc.resolve(from);

  if (isInCode(view.state)) {
    return false;
  }

  let textBefore = $from.parent.textBetween(
    Math.max(0, $from.parentOffset - MAX_MATCH),
    $from.parentOffset,
    undefined,
    '\ufffc',
  );

  if (textBefore.lastIndexOf('/') > -1) {
    textBefore = textBefore.substr(textBefore.lastIndexOf('/'));
  }

  const match = regex.exec(textBefore);
  const tr = handler(view.state, match, match ? from - match[0].length : from, to);
  if (!tr) return false;

  return true;
}

export const getTextOnlyNode = (view: EditorView) => {
  const addMenuItems = AddMenuPluginKey.getState(view.state)?.addMenuBlocks;

  return addMenuItems ? addMenuItems.filter((menuItem: blockType) => menuItem.isText) : [];
};

export const getTextAndMediaBlocks = (view: EditorView) => {
  const addMenuItems = AddMenuPluginKey.getState(view.state)?.addMenuBlocks;

  return addMenuItems
    ? addMenuItems.filter(
        (menuItem: blockType) =>
          menuItem.addMenuItemType === addMenuItemType.media || menuItem.isText,
      )
    : [];
};

export const filterAllowedAddMenuBlocks = (view: EditorView, allowedBlocks: string[]) => {
  const addMenuItems = AddMenuPluginKey.getState(view.state)?.addMenuBlocks;

  return addMenuItems
    ? addMenuItems.filter((menuItem: blockType) => allowedBlocks.includes(menuItem.name))
    : [];
};

export const addTextBlock = (view: EditorView, block: blockType) => {
  const nodes = view.state.schema.nodes;
  const newNode: NodeType = nodes[block.name];
  const { schema, selection } = view.state;
  // If we are in the list item, we will first move the item
  // out of the list and then convert it to particular text block.
  const parentList = findParentNode((node) => isList(node, schema))(selection);
  if (parentList) {
    liftListItem(nodes.list_item)(view.state, view.dispatch);
  }
  setBlockType(newNode, block.attrs)(view.state, view.dispatch);
};

export const addList = (view: EditorView, blockName: string) => {
  const nodes = view.state.schema.nodes;
  const newNode: NodeType = nodes[blockName];

  let togglePossible = toggleAddMenuList(newNode, nodes.list_item)(view.state, undefined);
  // It will happen when heading or quote node is in the selection. So we will
  // first convert it to paragraph and then convert it to the list.
  if (!togglePossible) {
    setBlockType(nodes.paragraph, {})(view.state, view.dispatch);
  }
  toggleAddMenuList(newNode, nodes.list_item)(view.state, view.dispatch);
};

export function toggleBlockquote(view: EditorView) {
  const { state, dispatch } = view;
  const { schema, selection } = state;
  const { blockquote } = schema.nodes;
  const { from, to } = selection;
  let tr = state.tr;
  tr = tr.setBlockType(from, to, blockquote);
  if (tr.docChanged) {
    dispatch(tr);
  }
  return true;
}

export const setPluginMeta = (view: EditorView, key: PluginKey, attrs: object) => {
  // @ts-ignore
  if (view?.docView) {
    view.dispatch(view.state.tr.setMeta(key, attrs));
  }
};

export const setNodeAttrs = (view: EditorView, pos: number, attrs: Object) => {
  view?.dispatch(view?.state.tr.setNodeMarkup(pos, undefined, attrs));
};

export const setNodeAttr = (view: EditorView, pos: number, attr: string, value: any) => {
  view?.dispatch(view?.state.tr.setNodeAttribute(pos, attr, value));
};

export const openAddMenu = (view: EditorView, position: number) => {
  const prevProps = AddMenuPluginKey.getState(view.state);
  let props = getAddMenuDefaultProps() as addMenuProps;
  const coordsPos = view.coordsAtPos(position) as any;
  const domNode = getDomNode(view, position);
  // @ts-ignore
  const getBoundingClientRect = domNode.getBoundingClientRect();
  props.offset = calculateOffset(coordsPos, getBoundingClientRect);
  props.placement = calculateTransformOrigin(coordsPos);
  props.open = true;
  props.editorView = view;
  props.anchorEl = domNode as PopperProps['anchorEl'];
  setPluginMeta(view, AddMenuPluginKey, { ...prevProps, ...props });
};

export const openAddMenuWithAnchorElement = ({
  view,
  anchorEl,
}: {
  view: EditorView;
  anchorEl: HTMLElement;
}) => {
  const prevProps = AddMenuPluginKey.getState(view.state);
  let props = getAddMenuDefaultProps() as addMenuProps;
  props.offset = [0, 0];
  props.placement = 'bottom-start';
  props.open = true;
  props.editorView = view;
  props.anchorEl = anchorEl;
  setPluginMeta(view, AddMenuPluginKey, { ...prevProps, ...props });
};

export const setSelection = (
  view: Pick<EditorView, 'dispatch' | 'state'>,
  selType: selectionType,
  from: number,
  to?: number,
) => {
  view.dispatch(
    view.state.tr.setSelection(
      selType === selectionType.TEXT
        ? new TextSelection(
            view.state.doc.resolve(from),
            Number.isInteger(to) ? view.state.doc.resolve(to!) : undefined,
          )
        : new NodeSelection(view.state.doc.resolve(from)),
    ),
  );
};

export const insertNode = (
  view: Pick<EditorView, 'dispatch' | 'state'>,
  selType: selectionType,
  nodeOText: string | Node | null,
  from: number,
  to?: number,
) => {
  view.dispatch(
    selType === selectionType.TEXT
      ? view.state.tr.insertText(nodeOText as string, from, Number.isInteger(to) ? to : undefined)
      : view.state.tr.insert(from, nodeOText as Node),
  );
};

export const getBlockMenuBaseLeftOffset = (anchorEl: PopperProps['anchorEl']) => {
  const nodeContainerClientRect = (anchorEl as Element)
    ?.querySelector(`.${NODE_CONTAINER_CLASSNAME}`)
    ?.getBoundingClientRect();
  // This is the outer container div width. Anchor element can't
  // exceed this container.
  const editorContainerInnerWidth = getEditorContainerInnerWidth();
  // When resizable component is used, we need to shift the blockmenu
  // and blockmenu button to the certain overflowing width.
  // We need to shift them by (element width - editor width) / 2.
  // It can be negative also if element if smaller than editor width.
  let baseLeftOffset = nodeContainerClientRect?.width
    ? (nodeContainerClientRect.width - getEditorWidth()!) / 2
    : 0;
  // If width of the element is near to full, we need to take care of
  // overflow of block-menu and block-menu-button and open the them
  // over the element.
  if (
    nodeContainerClientRect &&
    editorContainerInnerWidth &&
    nodeContainerClientRect.width + BLOCK_MENU_BUTTON_WIDTH + BLOCK_MENU_BUTTON_MARGIN >=
      editorContainerInnerWidth
  ) {
    baseLeftOffset = baseLeftOffset - BLOCK_MENU_BUTTON_WIDTH - BLOCK_MENU_BUTTON_MARGIN;
  }

  return baseLeftOffset;
};

export const addNewLine = (editorView: EditorView) => {
  insertNode(editorView, selectionType.NODE, editorView.state.schema.nodes.paragraph.create(), 0);
  setSelection(editorView, selectionType.TEXT, 1);
  editorView.focus();
};

export const isAdvancedItemEnabled = (selectedNode: Node) => {
  return !!selectedNode.attrs.src;
};

export const findNodeInDescendants = (node: Node, predicate: (node: Node) => boolean) => {
  let foundNode: { node: Node; pos: number } = { node: node, pos: 0 };
  let isFirstDescendant = true;

  node.descendants((node, pos) => {
    if (predicate(node)) {
      foundNode = { node, pos };
    }
    if (foundNode) {
      return false;
    }
    if (isFirstDescendant) {
      isFirstDescendant = false;
    }
  });

  return { foundNode, isFirstDescendant };
};

export const getPMDocFromHtml = (htmlString: string, schema: Schema) => {
  const htmlDoc = new DOMParser().parseFromString(htmlString, 'text/html');

  if (!htmlDoc.body.childNodes.length) {
    return null;
  }

  // need to wrap the contents in a dom node
  if (htmlDoc.body.children.length !== htmlDoc.body.childNodes.length) {
    htmlDoc.body.innerHTML = `<p>${htmlString}</p>`;
  }

  return PMDOMParser.fromSchema(schema).parse(htmlDoc.body);
};

export const removeMenuContainer = (selectorId: string, view?: EditorView) => {
  const dom = view?.dom?.parentNode?.querySelector(`#${selectorId}`);
  if (dom) {
    dom.remove();
  }
};

export function isSelected(view: EditorView): boolean {
  return view.state.selection.empty === false;
}

export function getPlainTextBetween(view: EditorView, from: number, to: number): string {
  return view.state.doc.textBetween(from, to, '\n', ' ');
}

export function getPlainTextFromSelection(view: EditorView): string {
  return getPlainTextBetween(view, view.state.selection.from, view.state.selection.to);
}

export function getPlainTextFromEditor(view: EditorView): string {
  return getPlainTextBetween(view, 0, view.state.doc.content.size);
}

export function setEditable(view: EditorView, editable: boolean): void {
  view.setProps({ editable: () => editable });
}

export function selectAll(view: EditorView): void {
  setSelection(view, selectionType.TEXT, 0, view.state.doc.content.size);
}

export function containsText(view: EditorView): boolean {
  return !!getPlainTextFromEditor(view).trim();
}

export function replaceTextSelection(view: EditorView, text: string, reselectText: boolean): void {
  const { from, to } = view.state.selection;
  insertNode(view, selectionType.TEXT, text, from, to);
  if (reselectText) {
    view.focus();
    setSelection(view, selectionType.TEXT, from, from + text.length);
  }
}

export function insertTextAfterSelection(
  view: EditorView,
  text: string,
  reselectText: boolean,
): void {
  const from = view.state.selection.to;
  insertNode(view, selectionType.TEXT, text, from, from);
  if (reselectText) {
    view.focus();
    setSelection(view, selectionType.TEXT, from, from + text.length);
  }
}

export function insertMarkdown(
  view: EditorView,
  markdownString: string,
  markdownParser: MarkdownParser,
) {
  const mdDoc = markdownParser.parse(markdownString);
  if (!view || !mdDoc) {
    return;
  }

  const slice = mdDoc.slice(0);
  const tr = view.state.tr;
  const from = view.state.selection.from;
  tr.replaceSelection(slice);
  // this newPos was always 2 greater than the doc size
  const newPos = from + slice.content.size - 2;
  view.dispatch(tr.scrollIntoView());
  view.focus();
  // setSelection(view, selectionType.TEXT, from, newPos);
}

function getMarksBetween(start: number, end: number, state: EditorState) {
  let marks: { start: number; end: number; mark: Mark }[] = [];

  state.doc.nodesBetween(start, end, (node, pos) => {
    marks = [
      ...marks,
      ...node.marks.map((mark) => ({
        start: pos,
        end: pos + node.nodeSize,
        mark,
      })),
    ];
  });

  return marks;
}

// ref: https://github.com/ueberdosis/tiptap/blob/d689e2d9c125fe0d74bdf19e890b061abe4e26a2/packages/core/src/inputRules/markInputRule.ts
export const markInputRule = (
  regexp: RegExp,
  markType: MarkType,
  getAttrs?: (match: string[]) => Record<string, unknown>,
): InputRule => {
  return new InputRule(
    regexp,
    (state: EditorState, match: string[], start: number, end: number) => {
      const attrs = getAttrs instanceof Function ? getAttrs(match) : getAttrs;
      const { tr } = state;
      const m = match.length - 1;
      let markEnd = end;
      let markStart = start;

      if (match[m]) {
        const matchStart = start + match[0].indexOf(match[m - 1]);
        const matchEnd = matchStart + match[m - 1].length - 1;
        const textStart = matchStart + match[m - 1].lastIndexOf(match[m]);
        const textEnd = textStart + match[m].length;

        const excludedMarks = getMarksBetween(start, end, state)
          .filter((item) => item.mark.type.excludes(markType))
          .filter((item) => item.end > matchStart);

        if (excludedMarks.length) {
          return null;
        }

        if (textEnd < matchEnd) {
          tr.delete(textEnd, matchEnd);
        }
        if (textStart > matchStart) {
          tr.delete(matchStart, textStart);
        }
        markStart = matchStart;
        markEnd = markStart + match[m].length;
      }

      tr.addMark(markStart, markEnd, markType.create(attrs));
      tr.removeStoredMark(markType);
      return tr;
    },
  );
};

export function htmlToPlaintext(html: string): string {
  return new DOMParser().parseFromString(html ?? '', 'text/html').body.textContent?.trim() ?? '';
}

export { AddMenuPluginKey, selectionType };

export function deleteAllBlocksOfType(editorView: EditorView, blockTypeName: string = 'paragraph') {
  const { state, dispatch } = editorView;
  const { doc, tr, schema } = state;

  const blockType = schema.nodes[blockTypeName];
  if (!blockType) {
    console.warn(`Node type '${blockTypeName}' not found in schema.`);
    return;
  }

  let pos = 0;
  for (let i = 0; i < doc.childCount; i++) {
    const node = doc.child(i);
    const nodeSize = node.nodeSize;

    if (node.type === blockType) {
      tr.delete(pos, pos + nodeSize);
    } else {
      pos += nodeSize;
    }
  }

  if (tr.docChanged) {
    dispatch(tr);
  }
}
